import React, { useCallback, useEffect, useRef, useState } from "react";
import { CardSelected } from "../../../type";
import { Cards } from "../../Cards/Cards";
import { AiFillStar } from "react-icons/ai";
import { FiEdit2 } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import {
  ConditionCodeText,
  ConditionWrapper,
  ContentWrapper,
  Group,
  HotKeyText,
} from "./modalChooseCardDetail.styled";
import {
  LabelStyled,
  PrimaryButton,
  SecondaryButton,
  StyledInput,
} from "../../common/common.style";
import { Modal } from "../../common/Modal/Modal";
import { Box, FlexBox, Image } from "../../primitives";
import { CARD_CONDITIONS_CARDTRADER, CARD_LANGUAGES } from "../../../constant";
import { useCards } from "../../../context/CardsContext";
import {
  CardTraderConditionValueType,
  CardTraderLanguageValueType,
} from "magic-shared-types";
import { useSelector } from "react-redux";
import { selectGame } from "../../../store/slices/global";
import { ReversIcon } from "../../common/CardsIconExtra/ReversIcon";
import { FirstEditionIcon } from "../../common/CardsIconExtra/FirstEditionIcon";
import { InfoTooltip } from "../../common/InfoTooltip/InfoTooltip";
import { CheckBox } from "../../common/CheckBox/CheckBox";

interface ModalChooseCardDetailProps {
  cardSelected: CardSelected;
  setCardsSelected: (arg1: CardSelected | null) => void;
  editCard: string | null;
  setEditCard: (arg1: string | null) => void;
  cardsBoard: CardSelected[];
  setCardsBoard: React.Dispatch<React.SetStateAction<CardSelected[]>>;
}

export const ModalChooseCardDetail = ({
  setCardsSelected,
  cardSelected,
  editCard,
  setEditCard,
  cardsBoard,
  setCardsBoard,
}: ModalChooseCardDetailProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cardComplexArray = useCards();
  const [isForceAllDetail, setIsForceAllDetail] = useState(true);
  const foundCardComplexArray = cardComplexArray[cardSelected.id];
  // const forceFoil = foundCardComplexArray?.nonfoil === false;
  // const forceNonFoil = foundCardComplexArray?.foil === false;
  // const disableFoilButton = !isForceAllDetail && (forceFoil || forceNonFoil);
  const game = useSelector(selectGame);
  const hasFoil = !["yugioh", "onepiece", "pokemon"].includes(
    game.card_trader_game
  );
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);
  const propertyLanguageBasedOnGame = `${game.card_trader_game}_language`;

  const allLanguageUsable = isForceAllDetail
    ? {
        name: propertyLanguageBasedOnGame,
        type: "string",
        default_value: "en",
        possible_values: CARD_LANGUAGES.map((lang) => lang.value),
      }
    : foundCardComplexArray?.editable_properties?.find(
        (property) => property.name === propertyLanguageBasedOnGame
      ) || {
        name: propertyLanguageBasedOnGame,
        type: "string",
        default_value: "en",
        possible_values: ["en"],
      };

  // const languageIsNotUsable = !allLanguageUsable?.possible_values?.includes(
  //  cardSelected.language
  // );

  const onEditCard = useCallback(() => {
    setCardsBoard((prevCards) =>
      prevCards.map((card, index) =>
        card.uid === editCard
          ? {
              ...(cardSelected as CardSelected),
              hasBeenEdited: true,
            }
          : { ...card }
      )
    );
    setEditCard(null);
  }, [editCard, cardSelected, setCardsBoard, setEditCard]);

  const addCardToCardsBoardHandler = useCallback(
    (cardSelected: CardSelected) => {
      setCardsBoard(cardsBoard.concat(cardSelected));
    },
    [cardsBoard, setCardsBoard]
  );

  /* useEffect(() => {
    let newLanguage = cardSelected.language;
    let newFinish = cardSelected.finish;
    if (languageIsNotUsable && allLanguageUsable) {
      newLanguage =
        allLanguageUsable?.default_value as CardTraderLanguageValueType;
    }

    if (forceFoil) {
      newFinish = "foil";
    }

    if (forceNonFoil) {
      newFinish = "regular";
    }

    setCardsSelected({
      ...cardSelected,
      language: newLanguage,
      finish: newFinish,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageIsNotUsable, forceFoil, forceNonFoil]); */

  const commonFunctionOnConfirm = useCallback(() => {
    if (editCard) {
      onEditCard();
    } else {
      addCardToCardsBoardHandler({ ...cardSelected, uid: uuidv4() });
    }
    setCardsSelected(null);
  }, [
    editCard,
    onEditCard,
    addCardToCardsBoardHandler,
    cardSelected,
    setCardsSelected,
  ]);

  const commonFunctionOnCancel = useCallback(() => {
    setCardsSelected(null);
    setEditCard(null);
  }, [setCardsSelected, setEditCard]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent hotkeys if typing inside an input or textarea
      const target = e.target as HTMLElement;
      if (
        (e.key !== "Enter" &&
          e.key !== "Escape" &&
          target.tagName === "INPUT" &&
          ["text", "search", "email", "url", "tel", "password"].includes(
            (target as HTMLInputElement).type
          )) ||
        target.tagName === "TEXTAREA"
      )
        return;

      const condition = CARD_CONDITIONS_CARDTRADER.find(
        (cond) => cond.hotKey === e.key.toLowerCase()
      );

      const language = CARD_LANGUAGES.find(
        (lang) =>
          lang.hotKey === e.key.toLowerCase() &&
          allLanguageUsable?.possible_values?.includes(lang.value)
      );

      if (e.key === "Enter") {
        commonFunctionOnConfirm();
      } else if (e.key === "Escape") {
        commonFunctionOnCancel();
      } else if (condition) {
        if (condition) {
          setCardsSelected({
            ...cardSelected,
            condition: condition.value as CardTraderConditionValueType,
          });
        }
      } else if (language) {
        setCardsSelected({
          ...cardSelected,
          language: language.value as CardTraderLanguageValueType,
        });
      } else if (e.key === "i" /* && !disableFoilButton */ && hasFoil) {
        setCardsSelected({
          ...cardSelected,
          finish: cardSelected.finish === "foil" ? "regular" : "foil",
        });
      } else if (e.key === "p") {
        setCardsSelected({
          ...cardSelected,
          signed: !cardSelected.signed,
        });
      } else if (e.key === "u" && hasReverse) {
        setCardsSelected({
          ...cardSelected,
          isReversed: !cardSelected.isReversed,
        });
      } else if (e.key === "o" && hasFirstEdition) {
        setCardsSelected({
          ...cardSelected,
          isFirstEdition: !cardSelected.isFirstEdition,
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    cardSelected,
    addCardToCardsBoardHandler,
    setCardsSelected,
    editCard,
    onEditCard,
    setEditCard,
    // disableFoilButton,
    allLanguageUsable?.possible_values,
    commonFunctionOnConfirm,
    commonFunctionOnCancel,
    hasFoil,
    hasReverse,
    hasFirstEdition,
  ]);

  return (
    <Modal onClose={() => commonFunctionOnCancel()}>
      <ContentWrapper>
        <Box css={{ margin: "auto" }}>
          {/* <Group>
            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>Force Detail Change</LabelStyled>
              <CheckBox
                checked={isForceAllDetail}
                setChecked={(checked: boolean) => {
                  setIsForceAllDetail(checked);
                }}
              />
            </FlexBox>
          </Group> */}

          <Group>
            {CARD_CONDITIONS_CARDTRADER?.map((condition) => {
              const isConditionSelected =
                condition.value === cardSelected.condition;

              return (
                <ConditionWrapper
                  isSelected={isConditionSelected}
                  key={condition.code}
                  onClick={() =>
                    setCardsSelected({
                      ...cardSelected,
                      condition:
                        condition.value as CardTraderConditionValueType,
                    })
                  }
                >
                  <ConditionCodeText
                    css={{
                      color: condition.color,
                    }}
                  >
                    {condition.code}
                  </ConditionCodeText>
                  <HotKeyText isSelected={isConditionSelected}>
                    {condition.hotKey}
                  </HotKeyText>
                </ConditionWrapper>
              );
            })}
          </Group>

          <Group>
            {CARD_LANGUAGES?.map((language) => {
              const isLanguageSelected =
                language.value === cardSelected.language;
              const isLanguageDisabled =
                !allLanguageUsable?.possible_values?.includes(language.value);

              return (
                <ConditionWrapper
                  isSelected={isLanguageSelected}
                  isDisabled={isLanguageDisabled}
                  key={language.value}
                  onClick={() => {
                    if (!isLanguageDisabled) {
                      setCardsSelected({
                        ...cardSelected,
                        language: language.value as CardTraderLanguageValueType,
                      });
                    }
                  }}
                >
                  <Image
                    src={language.flag}
                    css={{ width: 40 }}
                    alt={language.label}
                  />
                  <HotKeyText isSelected={isLanguageSelected}>
                    {language.hotKey}
                  </HotKeyText>
                </ConditionWrapper>
              );
            })}
          </Group>
          <Group>
            {hasReverse && (
              <InfoTooltip content="Reverse">
                <ConditionWrapper
                  isSelected={cardSelected.isReversed}
                  isDisabled={false}
                  onClick={() => {
                    setCardsSelected({
                      ...cardSelected,
                      isReversed: !cardSelected.isReversed,
                    });
                  }}
                >
                  <ReversIcon />
                  <HotKeyText isSelected={cardSelected.isReversed}>
                    U
                  </HotKeyText>
                </ConditionWrapper>
              </InfoTooltip>
            )}
            {hasFoil && (
              <InfoTooltip content="Foil">
                <ConditionWrapper
                  isSelected={cardSelected.finish === "foil"}
                  /* isDisabled={disableFoilButton}
                  onClick={() => {
                    if (!disableFoilButton) {
                      setCardsSelected({
                        ...cardSelected,
                        finish:
                          cardSelected.finish === "foil" ? "regular" : "foil",
                      });
                    }
                  }} */
                >
                  <AiFillStar size={24} color="#FFD700" />
                  <HotKeyText isSelected={cardSelected.finish === "foil"}>
                    I
                  </HotKeyText>
                </ConditionWrapper>
              </InfoTooltip>
            )}
            {hasFirstEdition && (
              <InfoTooltip content="First Edition">
                <ConditionWrapper
                  isSelected={cardSelected.isFirstEdition}
                  isDisabled={false}
                  onClick={() => {
                    setCardsSelected({
                      ...cardSelected,
                      isFirstEdition: !cardSelected.isFirstEdition,
                    });
                  }}
                >
                  <FirstEditionIcon />
                  <HotKeyText isSelected={cardSelected.isFirstEdition}>
                    O
                  </HotKeyText>
                </ConditionWrapper>
              </InfoTooltip>
            )}
            <InfoTooltip content="Signed">
              <ConditionWrapper
                isSelected={cardSelected.signed}
                onClick={() =>
                  setCardsSelected({
                    ...cardSelected,
                    signed: !cardSelected.signed,
                  })
                }
              >
                <FiEdit2
                  size={24}
                  color={cardSelected.signed ? "white" : "black"}
                />
                <HotKeyText isSelected={cardSelected.signed}>P</HotKeyText>
              </ConditionWrapper>
            </InfoTooltip>
          </Group>
          <Group>
            <PrimaryButton
              onClick={() => {
                commonFunctionOnConfirm();
              }}
            >
              <Box>CONFIRM</Box>
              <Box>( ENTER )</Box>
            </PrimaryButton>
            <SecondaryButton
              onClick={() => {
                commonFunctionOnCancel();
              }}
            >
              <Box>DELETE</Box>
              <Box>( ESC )</Box>
            </SecondaryButton>
          </Group>
        </Box>
        <FlexBox css={{ flexDirection: "column", gap: 10 }}>
          <Cards card={cardSelected} />
          <Box>
            <LabelStyled>Comments</LabelStyled>
            <StyledInput
              type="text"
              value={cardSelected.description}
              onChange={(e) => {
                setCardsSelected({
                  ...cardSelected,
                  description: e.target.value,
                });
              }}
              css={{ width: 330, marginTop: 10 }}
            />
          </Box>
          <StyledInput
            ref={inputRef}
            type="number"
            step={1}
            min={1}
            value={cardSelected.quantity}
            onChange={(e) => {
              setCardsSelected({
                ...cardSelected,
                quantity: +e.target.value || 0,
              });
            }}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              const blockedKeys = [
                "e",
                "E",
                "+",
                "-",
                ".",
                ",",
                ..."abcdefghijklmnopqrstuvwxyz".split(""),
              ];
              if (blockedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            css={{ width: 330, marginTop: 10 }}
          />
        </FlexBox>
      </ContentWrapper>
    </Modal>
  );
};
